import { Injectable, inject, ApplicationRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Idle, EventTargetInterruptSource } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { ApplicationSettingsService } from '../shared/services/application-settings.service';
import { ApplicationSettings } from '../models/application-settings.model';
import { AppConstants } from '../shared/app-constants';
import {Subject, Subscription, filter, catchError, timer, switchMap, retry} from 'rxjs';
import {SessionEventService} from '../shared/services/session-event-service';
import {Modal} from 'bootstrap';

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly idle = inject(Idle) as Idle;
  private readonly keepalive = inject(Keepalive) as Keepalive;
  private readonly router = inject(Router);
  private readonly appSettingsService = inject(ApplicationSettingsService);
  private readonly sessionEventService = inject(SessionEventService);



  private idleSeconds = AppConstants.DEFAULT_IDLE_TIMEOUT_DURATION_IN_MINUTES * 60;
  private warningSeconds = AppConstants.DEFAULT_WARNING_MINUTES_BEFORE_IDLE_TIMEOUT * 60;

  public showSimpleTimeoutModal$ = new Subject<{ idle: number; warning: number }>();
  public hideTimeoutModal$ = new Subject<void>();
  private modalAlreadyShown = false;

  private lastTrackedUrl = '';
  private isIdleWatcherActive = false;
  // Idle/keepalive event subscriptions of the current watcher - replaced (not stacked) on every re-init
  private idleEventSubs = new Subscription();
  private readonly appRef = inject(ApplicationRef);


  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.handleRouteChange());

    this.appRef.isStable.subscribe(stable => {
      if (stable) setTimeout(() => this.handleRouteChange(), 300);
    });
  }

  private handleRouteChange(): void {
    const url = this.router.url;
    if (url === '/') {
      this.stop();
      this.lastTrackedUrl = url;
      return;
    }

    const cameFromHome = this.lastTrackedUrl === '/';
    if (!this.isIdleWatcherActive || this.lastTrackedUrl !== url || cameFromHome) {
      this.lastTrackedUrl = url;
      this.isIdleWatcherActive = false;
      this.startWatching();
    }
  }

  startWatching(): void {
    if (this.router.url === '/') return;

    this.idle.stop();
    this.isIdleWatcherActive = false;
    this.modalAlreadyShown = false;

    this.proceedToIdleWatcher();
  }

  private proceedToIdleWatcher(): void {
    this.appSettingsService.fetchApplicationSettingsByDataCode('IDLE_TIMEOUT_DURATION_IN_MINUTES').pipe(
      catchError(error => {
        return timer(3000).pipe(
          switchMap(() =>
            this.appSettingsService.fetchApplicationSettingsByDataCode('IDLE_TIMEOUT_DURATION_IN_MINUTES')
          )
        );
      }),
      retry(2)
    ).subscribe({
      next: (idleSettingResponse: ApplicationSettings) => {
        const totalIdleMins = Number(idleSettingResponse?.dataValue || AppConstants.DEFAULT_IDLE_TIMEOUT_DURATION_IN_MINUTES);

        this.appSettingsService.fetchApplicationSettingsByDataCode('WARNING_MINUTES_BEFORE_IDLE_TIMEOUT').pipe(
          catchError(error => {
            return timer(3000).pipe(
              switchMap(() =>
                this.appSettingsService.fetchApplicationSettingsByDataCode('WARNING_MINUTES_BEFORE_IDLE_TIMEOUT')
              )
            );
          }),
          retry(2)
        ).subscribe({
          next: (warningSettingResponse: ApplicationSettings) => {
            const warningMins = Number(warningSettingResponse?.dataValue || AppConstants.DEFAULT_WARNING_MINUTES_BEFORE_IDLE_TIMEOUT);
            this.idleSeconds = (totalIdleMins - warningMins) * 60;
            this.warningSeconds = warningMins * 60;
            this.initializeIdleWatcher();
          },
          error: () => {
            this.idleSeconds = (totalIdleMins - AppConstants.DEFAULT_WARNING_MINUTES_BEFORE_IDLE_TIMEOUT) * 60;
            this.warningSeconds = AppConstants.DEFAULT_WARNING_MINUTES_BEFORE_IDLE_TIMEOUT * 60;
            this.initializeIdleWatcher();
          }
        });
      },
      error: () => {
        this.initializeIdleWatcher();
      }
    });
  }

  private initializeIdleWatcher(): void {
    if (this.isIdleWatcherActive) return;
    this.idle.setIdle(this.idleSeconds);
    this.idle.setTimeout(this.warningSeconds);
    this.idle.setInterrupts([
      new EventTargetInterruptSource(
        document,
        'mousemove keydown mousedown scroll touchstart click'
      )
    ]);

    this.idleEventSubs.unsubscribe();
    this.idleEventSubs = new Subscription();

    this.idleEventSubs.add(this.idle.onIdleStart.subscribe(() => {
      this.showModalOnce();
    }));

    this.idleEventSubs.add(this.idle.onTimeout.subscribe(() => {
      this.closeAllModals();
      this.hideTimeoutModal$.next();
      if (this.router.url.includes('court-deposit-details')) {
        this.sessionEventService.setSessionEvent('EXPIRED');
      } else {
        this.router.navigateByUrl("/");
        this.modalAlreadyShown = false;
      }
    }));

    this.idleEventSubs.add(this.idle.onIdleEnd.subscribe(() => {
      this.modalAlreadyShown = false;
    }));

    this.keepalive.interval(15);
    this.idleEventSubs.add(this.keepalive.onPing.subscribe(() => {}));

    this.idle.watch();
    this.isIdleWatcherActive = true;
  }

  private showModalOnce(): void {
    if (this.modalAlreadyShown) return;
    this.modalAlreadyShown = true;

    const idleMins = Math.ceil((this.idleSeconds + this.warningSeconds) / 60);
    const warnMins = Math.ceil(this.warningSeconds / 60);

    this.showSimpleTimeoutModal$.next({ idle: idleMins, warning: warnMins });
  }

  reset(): void {
    this.idle.watch();
  }

  stop(): void {
    this.idle.stop();
    this.modalAlreadyShown = false;
  }

  onModalOk(): void {
    this.modalAlreadyShown = false;
    this.isIdleWatcherActive = false;
  }

  private closeAllModals(): void {
    // Close any open modals by force
    const modals = document.querySelectorAll('.modal.show');
    modals.forEach((modalElement) => {
      const modalInstance = Modal.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    });

    // Remove any leftover modal backdrops
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach((backdrop) => backdrop.remove());

    // Remove scroll lock and reset overflow
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
  }
}
